/**
 * <Alert> — inline semantic banner (NOT a toast).
 *
 * A static alert surface for contextual messages (info / success / warning /
 * danger). Unlike Toast, an Alert sits in the document flow and persists until
 * the consumer removes it.
 *
 * Variants per UI-SPEC §Color semantic palette use soft backgrounds + matching
 * Lucide icon (via `<Icon>`). The consumer can override the icon via `icon`.
 *
 * ARIA: `role="alert"` + `aria-live="polite"` by default — screen readers
 * announce on mount. For non-time-critical messages (e.g. "Saved"), consumers
 * can override `aria-live="off"`.
 */
import * as React from "react";
import { AlertOctagon, AlertTriangle, CheckCircle, Info, type LucideProps } from "lucide-react";
import { cn, cva, type VariantProps } from "../../utils";
import { Icon } from "../Icon";

type LucideIconComponent = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

export const alertVariants = cva(
  cn("relative flex items-start gap-sm", "rounded-md border p-md text-body"),
  {
    variants: {
      variant: {
        info: "bg-info-soft border-info/40",
        success: "bg-success-soft border-success/40",
        warning: "bg-warning-soft border-warning/40",
        danger: "bg-danger-soft border-danger/40",
      },
    },
    defaultVariants: { variant: "info" },
  },
);

export type AlertVariant = NonNullable<VariantProps<typeof alertVariants>["variant"]>;

const VARIANT_ICON: Record<AlertVariant, LucideIconComponent> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  danger: AlertOctagon,
};

const VARIANT_ICON_NAME: Record<AlertVariant, string> = {
  info: "info",
  success: "check-circle",
  warning: "alert-triangle",
  danger: "alert-octagon",
};

const VARIANT_ICON_COLOR: Record<AlertVariant, string> = {
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

export interface AlertProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "role" | "title">,
    VariantProps<typeof alertVariants> {
  /** Optional heading rendered above the description. */
  title?: React.ReactNode;
  /** Body content — the primary message. */
  description: React.ReactNode;
  /** Custom icon slot — overrides the variant default. Pass `null` to suppress. */
  icon?: React.ReactNode | null;
  /**
   * Alert renders `title` + `description` ONLY — it has no children slot.
   *
   * Typing this as `never` turns what used to be a silent drop into a compile
   * error. Two call sites had passed children and had them swallowed with no
   * warning: the appraisal DRAFT banner (its "Continue setup" button never
   * rendered, so a draft cycle had no way forward at all) and the attendance
   * DayDrawer (its Retry button never rendered, so a failed load was a dead
   * end). Both were invisible to typecheck, lint and tests because
   * `HTMLAttributes<HTMLDivElement>` admits `children` by default.
   *
   * Put actionable content inside `description` — it accepts a ReactNode.
   */
  children?: never;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { className, variant, title, description, icon, ...rest },
  ref,
) {
  const resolvedVariant = variant ?? "info";
  const IconComp = VARIANT_ICON[resolvedVariant];
  const iconName = VARIANT_ICON_NAME[resolvedVariant];
  const iconColor = VARIANT_ICON_COLOR[resolvedVariant];

  const iconNode =
    icon === null ? null : icon !== undefined ? (
      icon
    ) : (
      <span className={cn("mt-[2px] inline-flex shrink-0", iconColor)}>
        <Icon as={IconComp} name={iconName} size={18} />
      </span>
    );

  return (
    <div
      ref={ref}
      role="alert"
      aria-live="polite"
      className={cn(alertVariants({ variant: resolvedVariant }), className)}
      {...rest}
    >
      {iconNode}
      <div className="flex-1">
        {title !== undefined && <div className="text-heading text-body font-semibold">{title}</div>}
        <div className="text-body">{description}</div>
      </div>
    </div>
  );
});
